import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import type { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import { CheckIcon, PencilIcon, PlusIcon } from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

import { Cell, DataTable, useDataTable } from '@/components/block/data-table'
import { Page } from '@/components/layout/page'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CardExtendTable } from '@/components/ui/card-extend'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import { itemMasterApi } from '@/features/inventory/api/item-master-api'
import { itemOutletApi } from '@/features/inventory/api/item-outlet-api'
import type { ItemMasterDto } from '@/features/inventory/dto/item-master-dto'

import { useAuth } from '@/lib/auth'
import { buildToastMessage } from '@/lib/toast-message'
import { toIdr } from '@/libs/utils/format'

export const Route = createFileRoute('/_app/inventory/master/')({
  component: RouteComponent,
})

function RouteComponent() {
  return (
    <Page>
      <Page.Breadcrumbs />
      <MasterTable />
    </Page>
  )
}

function MasterTable() {
  const outletId = useAuth().outlet.oId
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const { data, isLoading } = useQuery({
    ...itemMasterApi.list.query({}),
    select: ({ data }) =>
      data.items.map(item => ({
        ...item,
        assigned: item.outletIds.includes(outletId),
      })),
  })

  const columns = useMemo(() => getColumns(), [])
  const table = useDataTable({
    columns,
    isLoading,
    data: data ?? [],
    state: {
      rowSelection,
    },
    onRowSelectionChange: setRowSelection,
    getRowId: row => row.id,
  })

  const selectedIds = useMemo(() => {
    return Object.entries(rowSelection).reduce((acc, [key, value]) => {
      if (value) acc.push(key)
      return acc
    }, [] as string[])
  }, [rowSelection])

  return (
    <CardExtendTable
      title='Daftar Master Bahan'
      action={
        <>
          <Button
            variant='secondary'
            type='button'
            onClick={() => setIsDialogOpen(true)}
            disabled={selectedIds.length === 0}
          >
            Assign Ke Outlet
          </Button>
          <Button asChild>
            <Link from={Route.fullPath} to='/inventory/master/create'>
              <PlusIcon /> Tambah Bahan Baku
            </Link>
          </Button>
        </>
      }
    >
      <DataTable.Root table={table}>
        <DataTable.Table />
        <DataTable.Pagination />
      </DataTable.Root>
      <DialogAssignment
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSuccess={() => {
          setRowSelection({})
          setIsDialogOpen(false)
        }}
        getItems={() => {
          if (!data || !selectedIds.length) return []
          return data?.filter(x => selectedIds.includes(x.id))
        }}
      />
    </CardExtendTable>
  )
}

interface DialogAssignmentProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  getItems: () => ItemMasterDto[]
}

function DialogAssignment({ open, onClose, onSuccess, getItems }: DialogAssignmentProps) {
  const { outlets, outlet } = useAuth()

  const { mutateAsync, isPending } = useMutation({
    mutationFn: itemOutletApi.assign.fetch,
  })

  const items = useMemo(() => {
    return getItems()
  }, [open, getItems])

  const handleOnOpenChange = () => {
    if (isPending) return
    onClose()
  }

  const handleConfirmAssignment = async () => {
    const mutation = mutateAsync({
      body: {
        oId: outlet?.oId,
        imIds: items.map(item => item.id),
      },
    })

    await toast.promise(mutation, buildToastMessage('create', 'Assign Item')).unwrap()
    onSuccess()
  }

  return (
    <Dialog open={open} onOpenChange={handleOnOpenChange}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>Assign {items.length} Item(s)</DialogTitle>
        </DialogHeader>
        <div className='grid gap-4 py-1'>
          <div className='space-y-2'>
            <Label htmlFor='outlet-select'>Pilih Outlet Tujuan</Label>
            <Select defaultValue={outlet?.oId}>
              <SelectTrigger className='w-full'>
                <SelectValue placeholder='Pilih Outlet' />
              </SelectTrigger>
              <SelectContent>
                {outlets.map(outlet => (
                  <SelectItem key={outlet.oId} value={outlet.oId}>
                    {outlet.outlet.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className='space-y-2'>
              <Label>Daftar bahan baku</Label>
              <ScrollArea className='max-h-64 rounded-sm border border-border'>
                <div className='divide-y divide-border'>
                  {items.map(item => (
                    <div key={item.id} className='px-2.5 py-2'>
                      <p className='text-sm'>{item.name}</p>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          </div>
        </div>
        <div className='flex justify-end'>
          <Button onClick={handleConfirmAssignment} disabled={items.length === 0 || isPending}>
            Simpan
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function getColumns(): ColumnDef<ItemMasterDto & { assigned: boolean }>[] {
  return [
    {
      id: 'select',
      accessorFn: row => row.id,
      size: 60,
      minSize: 40,
      maxSize: 40,
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={value => table.toggleAllPageRowsSelected(!!value)}
          aria-label='Select all'
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={value => row.toggleSelected(!!value)}
          aria-label='Select row'
          onClick={e => e.stopPropagation()}
        />
      ),
    },
    {
      accessorKey: 'name',
      header: 'Nama',
      minSize: 180,
      cell: ({ row }) => (
        <Cell.Link
          from={Route.fullPath}
          to='/inventory/master/$id/update'
          params={{ id: row.original.id }}
        >
          {row.original.name}
        </Cell.Link>
      ),
    },
    {
      accessorKey: 'assigned',
      header: 'Assigned',
      maxSize: 100,
      cell: ({ row }) => {
        const outletId = useAuth().outlet.oId
        const isAssigned = row.original.outletIds.includes(outletId)
        return <Cell>{isAssigned ? <CheckIcon className='size-4 text-success' /> : '-'}</Cell>
      },
    },
    {
      accessorKey: 'recipeId',
      header: 'Resep',
      cell: ({ row }) => {
        const hasRecipe = !!row.original.recipeId

        return (
          <Button asChild variant='ghost' size='icon-sm'>
            <Link
              from={Route.fullPath}
              to='/inventory/recipe'
              search={{ refId: row.original.id, refType: 'item' }}
            >
              {hasRecipe ? <PencilIcon /> : <PlusIcon />}
            </Link>
          </Button>
        )
      },
    },
    {
      accessorKey: 'type',
      header: 'Jenis',
      cell: ({ row }) => (
        <Cell>
          <Badge variant={row.original.type === 'raw' ? 'green' : 'sky'}>{row.original.type}</Badge>
        </Cell>
      ),
    },
    {
      accessorKey: 'baseCost',
      header: 'Harga',
      cell: ({ row }) => <Cell.Text text={toIdr(row.original.baseCost)} />,
    },
    {
      accessorKey: 'baseUnit',
      header: 'Satuan',
      cell: ({ row }) => <Cell.Text text={row.original.units[0].code} />,
    },
    {
      accessorKey: 'updatedAt',
      header: () => 'Updated At',
      cell: ({ row }) => <Cell.Date date={row.original.updatedAt} />,
    },
    {
      accessorKey: 'createdAt',
      header: () => 'Created At',
      cell: ({ row }) => <Cell.Date date={row.original.createdAt} />,
    },
  ]
}
